import React, { type FC } from 'react';

import { gettext } from '@eventbrite/i18n';
import {
    CompactCalendar,
    FooterLayout,
    ScrollableCalendar,
    type CalendarProps,
} from '@eventbrite/timed-entry';

import { InfiniteCalendarSeriesDispatcher } from '../../../actions/infiniteCalendarSeries';
import { isCompactCalendar } from '../../../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import {
    CalendarType,
    DateEventStatus,
    type DateAvailabilityStatus,
    type InfiniteCalendarSeriesNamespace,
} from '../../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import commonStyles from './CalendarContainer.common.module.scss';
import defaultScrollableStyles from './CalendarContainer.module.scss';
import rebrandScrollableStyles from './CalendarContainer.rebranding.module.scss';
import { getCalendarDateCellProps, getFooterSignals } from './CalendarProps';
import defaultCompactCalendarStyles from './CompactCalendar.module.scss';
import rebrandingCompactCalendarStyles from './CompactCalendar.rebranding.module.scss';
import { trimISODatetimeToDate } from './utils';

const compactStylesModules = {
    [DEFAULT_STYLES]: defaultCompactCalendarStyles,
    [REBRANDING_STYLES]: rebrandingCompactCalendarStyles,
};

const scrollableStylesModules = {
    [DEFAULT_STYLES]: defaultScrollableStyles,
    [REBRANDING_STYLES]: rebrandScrollableStyles,
};

type CalendarContainerProps = Required<
    Pick<
        CalendarProps<DateAvailabilityStatus>,
        | 'firstMonth'
        | 'initialSelectedDate'
        | 'lastMonth'
        | 'locale'
        | 'onMonthLoad'
    >
> &
    Pick<
        InfiniteCalendarSeriesNamespace,
        | 'calendarType'
        | 'compactCalendarData'
        | 'isAppRebrandActive'
        | 'scrollCalendarData'
        | 'calendarDates'
        | 'currency'
    > &
    Pick<InfiniteCalendarSeriesDispatcher, 'onDateSelect'> & {
        showFooter: boolean;
    };

export const CalendarContainer: FC<CalendarContainerProps> = ({
    calendarType,
    scrollCalendarData,
    compactCalendarData,
    initialSelectedDate,
    locale,
    firstMonth,
    lastMonth,
    onDateSelect,
    onMonthLoad,
    isAppRebrandActive,
    showFooter,
    calendarDates,
    currency,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const compactCalendarStyles = compactStylesModules[theme];
    const scrollableStyles = scrollableStylesModules[theme];
    const styles =
        calendarType === CalendarType.scrollCalendar
            ? scrollableStyles
            : compactCalendarStyles;
    const calendar =
        calendarType === CalendarType.scrollCalendar ? (
            <ScrollableCalendar
                calendarData={scrollCalendarData}
                dateCellProps={getCalendarDateCellProps(isAppRebrandActive)}
                initialSelectedDate={initialSelectedDate}
                locale={locale}
                firstMonth={firstMonth}
                lastMonth={lastMonth}
                onMonthLoad={onMonthLoad}
                onDateSelect={(dateString) => {
                    onDateSelect(dateString, calendarType);
                }}
                defaultDateCellKey={DateEventStatus.unavailable}
                monthNameClassName={styles.monthName}
                weekdayNameClassName={styles.weekday}
                dateTextClassName={styles.dateText}
                enabledDateCellClassName={styles.enabledDateCell}
                selectedDateCellClassName={styles.selectedDateCell}
                selectedDateCellIconClassName={styles.selectedDateCellIcon}
                todayDateCellClassName={styles.todayDateCell}
                scrollCueMessage={gettext('More dates below').toString()}
            />
        ) : isCompactCalendar(calendarType) ? (
            <div className={commonStyles.compactCalendarContainer}>
                <CompactCalendar
                    calendarData={compactCalendarData}
                    initialSelectedDate={trimISODatetimeToDate(
                        initialSelectedDate,
                    )}
                    locale={locale}
                    onDateSelect={(dateString, id, sessionTime) => {
                        onDateSelect(dateString, calendarType, id, sessionTime);
                    }}
                    selectedClassName={styles.selectedDateCell}
                    dateTextClassName={styles.dateText}
                    weekdayTextClassName={styles.weekday}
                    timeSlotTextClassName={styles.timeSlotText}
                    monthLabelClassName={styles.monthName}
                />
            </div>
        ) : null;

    return (
        <>
            {calendar}
            {showFooter ? (
                <div className={commonStyles.footerLayout}>
                    <FooterLayout
                        footerMetaData={getFooterSignals({
                            isAppRebrandActive,
                            calendarType,
                            calendarDates,
                            currency,
                        })}
                    />
                </div>
            ) : null}
        </>
    );
};
